import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuthenticatedUser, CallerPreferences } from "@yazamco/shared-types";
import { fetchMe } from "../lib/auth";
import { SESSION_EXPIRED_EVENT } from "../lib/httpClient";

export interface AuthState {
  user: AuthenticatedUser | null;
  /**
   * Caller-scoped client preferences, served alongside the identity.
   *
   * They used to be read from the Settings module, which forced every
   * authenticated principal to hold `settings.read` just to learn its own polling
   * interval. Serving them here decouples the two: Settings is now genuinely
   * administrative, and a principal with no Settings access still refreshes.
   */
  preferences: CallerPreferences | null;
  isLoading: boolean;
  isError: boolean;
  /** True after the server invalidated the session (drives the timeout screen). */
  sessionExpired?: boolean;
}

// Exported so tests can supply a value directly without hitting the network.
export const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Provides the current authenticated user by calling GET /api/v1/me. A 401
 * resolves to `user: null` (unauthenticated → the app shows the login screen),
 * not an error. Listens for the httpClient's session-expired broadcast so an
 * expired session surfaces the timeout screen and clears the cached user.
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [sessionExpired, setSessionExpired] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    function onExpired(): void {
      setSessionExpired(true);
      queryClient.setQueryData(["me"], null);
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [queryClient]);

  // Clear the expired flag once a user is present again (after re-login).
  useEffect(() => {
    if (data?.user) setSessionExpired(false);
  }, [data]);

  return (
    <AuthContext.Provider
      value={{
        user: data?.user ?? null,
        preferences: data?.preferences ?? null,
        isLoading,
        isError,
        sessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * The auth state, or null when there is no provider above.
 *
 * For consumers where authentication is incidental rather than required — the
 * polling cadence being the case in point. A refresh interval must never be able
 * to crash a component tree, so it asks without insisting.
 */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext) ?? null;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}
